import React, { useMemo } from "react"
import styled from "~/ui/styled"
import {
  BetweenFlex,
  Button,
  CenterFlex,
  ColumnFlex,
  IconFont,
  StartFlex,
  Text,
} from "~components/El"
import { useTheme } from "~/ui/styled"
import { formatMessageTime } from "~utils"
import type { NoticeItem } from "./notice-center-context"
import {
  getPendingCollectConfirmText,
  getPendingCollectContent,
  getPendingCollectKind,
  getPendingCollectTitle,
} from "./notice-collect-navigation"

const Card = styled(ColumnFlex)`
  width: 100%;
  min-height: 0;
`

const CardBody = styled.div`
  flex: 1;
  min-height: 0;
  overflow: auto;
  padding: 20px 24px;
`

const CardFooter = styled(BetweenFlex)`
  flex-shrink: 0;
  padding: 16px 24px 20px;
  gap: 12px;
  border-top: 1px solid ${({ theme }) => theme.colors.border};
`

const GiftIcon = styled(CenterFlex)`
  width: 36px;
  height: 36px;
  border-radius: 18px;
  background-color: ${({ theme }) => theme.colors.primary};
  flex-shrink: 0;
`

type Props = {
  item: NoticeItem
  detailData: any
  onClose: () => void
  onConfirm: () => void
}

const NoticePendingCollectCard: React.FC<Props> = ({
  item,
  detailData,
  onClose,
  onConfirm,
}) => {
  const theme = useTheme()
  const kind = useMemo(() => getPendingCollectKind(item, detailData), [item, detailData])
  const isRead = item.is_read === true

  if (!kind) return null

  const title = getPendingCollectTitle(item, kind)
  const content = getPendingCollectContent(item, detailData, kind)
  const confirmText = getPendingCollectConfirmText(kind)

  return (
    <Card height="auto">
      <CardBody>
        <StartFlex width="100%" alignItems="flex-start" gap="10px">
          <GiftIcon>
            <IconFont name="hb" size={18} color={theme.colors.blockBackground} />
          </GiftIcon>
          <ColumnFlex flex={1} minWidth={0} height="auto">
            <Text fontSize="18px" fontWeight="600" color={theme.colors.text}>
              {title}
            </Text>
            <StartFlex mt="8px" gap="12px" flexWrap="wrap">
              <StartFlex width="auto">
                <IconFont name="sssj" size={12} color={theme.colors.secondaryText} />
                <Text fontSize="12px" color={theme.colors.secondaryText} ml="6px">
                  {formatMessageTime(item.created)}
                </Text>
              </StartFlex>
              <StartFlex width="auto">
                {isRead ? (
                  <IconFont name="szyx1" size={12} color={theme.colors.primary} />
                ) : (
                  <CenterFlex
                    width="8px"
                    height="8px"
                    bg={theme.colors.primaryText}
                    borderRadius="4px"
                  />
                )}
                <Text
                  fontSize="12px"
                  color={isRead ? theme.colors.primary : theme.colors.primaryText}
                  ml="6px"
                >
                  {isRead ? "已读" : "未读"}
                </Text>
              </StartFlex>
            </StartFlex>
          </ColumnFlex>
        </StartFlex>

        <Text fontSize="15px" color={theme.colors.text} mt="20px" lineHeight="22px">
          {content}
        </Text>
      </CardBody>

      <CardFooter height="auto">
        <Button
          flex={1}
          height="44px"
          borderRadius="8px"
          bg={theme.colors.background}
          onClick={onClose}
        >
          <Text color={theme.colors.text}>关闭</Text>
        </Button>
        <Button
          flex={1}
          height="44px"
          borderRadius="8px"
          bg={theme.colors.primary}
          onClick={onConfirm}
        >
          <Text color={theme.colors.blockBackground}>{confirmText}</Text>
        </Button>
      </CardFooter>
    </Card>
  )
}

export default NoticePendingCollectCard
